import React from "react";
import { Button, Col, Row, Form as BTForm, Spinner } from "react-bootstrap";
import axios from "../../../../config/axios";
import OnlineReservationSteps from "../../OnlineReservationSteps";
import { showValidationErrorToast } from "../../../../components/Toasts";
import useOnlineReservation from "../../hooks/useOnlineReservation";
import { dateFormatter, euroFormatter } from "../../../../global";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { linkBuilder } from "../../../../global/navigationHelper";
import { Form, Formik } from "formik";
import FormInputText from "../../../../components/Forms/FormInputText";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleInfo } from "@fortawesome/free-solid-svg-icons";
import useModal from "../../../../components/Modal/hooks/useModal";
import LocalTaxInfoDialog from "../../LocalTaxInfoDialog";

const OnlineReservationOverviewPage = () => {
  const { onlineReservation } = useOnlineReservation();
  const { token } = useParams();
  const { t } = useTranslation("reservations");
  const { isShow: showLocalTaxDialog, toggle: toggleLocalTaxDialog } =
    useModal();

  function handleSubmit(values, options) {
    axios
      .post(`/online_reservations/${token}/request_reservation.json`, {
        comment: values.comment,
        payment_type: values.payment_type,
      })
      .then(function (response) {
        window.dataLayer.push({
          event: "bookingConfirmation",
          revenue: onlineReservation.deposit_price,
          transactionId: token,
        });

        if (values.payment_type === "stripe") {
          axios
            .post(`/stripe/checkout_sessions`, { token: token })
            .then(({ data }) => {
              window.location.assign(data.url);
              options.setSubmitting(false);
            })
            .catch((error) => {
              showValidationErrorToast(error);
              options.setSubmitting(false);
            });
        } else {
          const redirectUrl = response.data.redirect_url;
          window.location.assign(redirectUrl);
        }
      })
      .catch(function (error) {
        showValidationErrorToast(error);
        options.setSubmitting(false);
      });
  }

  if (!onlineReservation) {
    return <></>;
  }
  return (
    <>
      <OnlineReservationSteps step={3} />
      <Formik
        initialValues={{ comment: onlineReservation.booking.comment }}
        enableReinitialize={true}
        onSubmit={handleSubmit}
      >
        {({ values, setFieldValue, isSubmitting }) => (
          <Form>
            <Row className={"mb-3"}>
              <Col>
                <Row>
                  <Col>
                    <div className={"d-flex justify-content-between"}>
                      <span>{t("overview.arrival")}:</span>
                      <span>{dateFormatter(onlineReservation.from)}</span>
                    </div>
                  </Col>
                  <Col>
                    <div className={"d-flex justify-content-between"}>
                      <span>{t("overview.departure")}:</span>
                      <span>{dateFormatter(onlineReservation.to)}</span>
                    </div>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <div className={"d-flex justify-content-between"}>
                      <span>{t("editContactDetails.first_name.label")}:</span>
                      <span>{onlineReservation.contact_first_name}</span>
                    </div>
                  </Col>
                  <Col>
                    <div className={"d-flex justify-content-between"}>
                      <span>{t("editContactDetails.last_name.label")}:</span>
                      <span>{onlineReservation.contact_last_name}</span>
                    </div>
                  </Col>
                </Row>
                <Row className={"mb-3"}>
                  <Col>
                    <div className={"d-flex justify-content-between"}>
                      <span>{t("editContactDetails.email.label")}:</span>
                      <span>{onlineReservation.contact_email}</span>
                    </div>
                  </Col>
                  <Col>
                    <div className={"d-flex justify-content-between"}>
                      <span>{t("editContactDetails.phone.label")}:</span>
                      <span>{onlineReservation.contact_phone}</span>
                    </div>
                  </Col>
                </Row>
                <Row className={"mb-5"}>
                  <Col>
                    <h4>{t("overview.priceOverview")}</h4>
                    <div className={"d-flex justify-content-between"}>
                      <span>
                        {t("common:online_reservation.parking_slot")}:
                      </span>
                      <span>
                        {onlineReservation.parking_slot?.name ||
                          t("overview.noParkingSlotSelected")}
                      </span>
                    </div>
                    <div className={"d-flex justify-content-between"}>
                      <span>{t("overview.parkingSlotPrice")}:</span>
                      <span>{euroFormatter(onlineReservation.day_price)}</span>
                    </div>
                    <div className={"d-flex justify-content-between"}>
                      <span>{t("overview.personPrice")}:</span>
                      <span>
                        {euroFormatter(onlineReservation.person_price)}
                      </span>
                    </div>
                    <div className={"d-flex justify-content-between"}>
                      <span>{t("overview.dogPrice")}:</span>
                      <span>{euroFormatter(onlineReservation.dog_price)}</span>
                    </div>
                    <div className={"d-flex justify-content-between"}>
                      <span>
                        <span className={"mr-1"}>
                          {t("overview.localTaxPrice")}
                        </span>
                        <FontAwesomeIcon
                          className={"cursor-pointer"}
                          icon={faCircleInfo}
                          onClick={toggleLocalTaxDialog}
                        />
                      </span>
                      <span>
                        {euroFormatter(onlineReservation.local_tax_price)}
                      </span>
                    </div>
                    <div className={"d-flex justify-content-between"}>
                      <span>{t("overview.totalPrice")}:</span>
                      <span>{euroFormatter(onlineReservation.price)}</span>
                    </div>
                    <div className={"d-flex justify-content-between"}>
                      <span>{t("overview.depositPrice")}:</span>
                      <span>
                        {euroFormatter(onlineReservation.deposit_price)}
                      </span>
                    </div>
                  </Col>
                </Row>
                <Row className={"mb-3"}>
                  <Col>
                    <FormInputText
                      style={{ minHeight: "50px" }}
                      as={"textarea"}
                      name={"comment"}
                      label={t("overview.comment.label")}
                      placeholder={t("overview.comment.placeholder")}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <h4>{t("overview.paymentOptions.label")}</h4>
                    {onlineReservation.configuration.stripe_enabled && (
                      <BTForm.Check
                        checked={values.payment_type === "stripe"}
                        onChange={() => setFieldValue("payment_type", "stripe")}
                        name={"payment_type"}
                        type={"radio"}
                        id={"stripe"}
                        label={t("overview.paymentOptions.stripe")}
                      />
                    )}

                    {onlineReservation.configuration.bank_transfer_enabled && (
                      <BTForm.Check
                        checked={values.payment_type === "bank_transfer"}
                        onChange={() =>
                          setFieldValue("payment_type", "bank_transfer")
                        }
                        name={"payment_type"}
                        type={"radio"}
                        id={"bank_transfer"}
                        label={t("overview.paymentOptions.bank_transfer")}
                      />
                    )}
                  </Col>
                </Row>
              </Col>
              <Col md={3}></Col>
            </Row>
            <Row>
              <Col md={9}>
                <div className={"d-flex justify-content-between mb-3"}>
                  <Link
                    to={linkBuilder(
                      `/reservations/${onlineReservation.token}/contact-details`
                    )}
                  >
                    <Button variant={"secondary"}>
                      {t("common:components.button.back")}
                    </Button>
                  </Link>
                  <Button type="submit" disabled={isSubmitting}>
                    {isSubmitting && <Spinner className={"mr-2"} size={"sm"} />}
                    {t("overview.bookNow")}
                  </Button>
                </div>
              </Col>
            </Row>
          </Form>
        )}
      </Formik>
      {showLocalTaxDialog && (
        <LocalTaxInfoDialog handleClose={toggleLocalTaxDialog} />
      )}
    </>
  );
};

export default OnlineReservationOverviewPage;
